/**
 * Homepage Scroll Story - One fixed 100vh viewport
 * Hero + Circle overlap: circle fades in as soon as you start scrolling; hero stays until story
 * Circle rotates and scales with scroll
 */
class HomepageScrollStory extends HTMLElement {
  constructor() {
    super();
    this.rafId = null;
    this.onScroll = this.onScroll.bind(this);
    this.update = this.update.bind(this);
  }

  connectedCallback() {
    this._onResize = () => {
      this.measure();
      this.update();
    };
    this.measure();
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this._onResize);
    this.update();
    requestAnimationFrame(() => this.measure());
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this._onResize);
    if (this.rafId) cancelAnimationFrame(this.rafId);
  }

  measure() {
    const rect = this.getBoundingClientRect();
    this.sectionTop = rect.top + window.scrollY;
    const totalVh = parseFloat(getComputedStyle(this).getPropertyValue('--total-scroll-vh')) || 1100;
    this.sectionHeight = totalVh * (window.innerHeight / 100);
  }

  onScroll() {
    if (this.rafId) return;
    this.rafId = requestAnimationFrame(() => {
      this.update();
      this.rafId = null;
    });
  }

  update() {
    const scrollY = window.scrollY;
    const vh = window.innerHeight;

    const localScroll = scrollY - this.sectionTop;
    const localScrollVh = (localScroll / vh) * 100;

    const heroScene = this.querySelector('.scroll-story__scene--hero');
    const circleScene = this.querySelector('.scroll-story__scene--circle');
    const slideScenes = this.querySelectorAll('.scroll-story__scene--slide');

    if (!heroScene || !circleScene) return;

    const storyStartVh = 300;

    // Hero: visible 0 to storyStart, fade out only when transitioning to story (280-300vh)
    const heroFadeStart = storyStartVh - 20;
    let heroOpacity = 1;
    if (localScrollVh < 0) heroOpacity = 1;
    else if (localScrollVh > heroFadeStart) {
      heroOpacity = Math.max(0, 1 - (localScrollVh - heroFadeStart) / 20);
    }
    heroScene.style.opacity = heroOpacity;
    heroScene.style.visibility = heroOpacity > 0.01 ? 'visible' : 'hidden';
    heroScene.style.pointerEvents = heroOpacity > 0.01 ? 'auto' : 'none';

    // Hero title: start slightly smaller (0.88), grow to 1 over first 70vh
    const heroScaleRangeVh = 70;
    const heroScaleStart = 0.88;
    const heroScale = localScrollVh < 0
      ? heroScaleStart
      : localScrollVh >= heroScaleRangeVh
        ? 1
        : heroScaleStart + (1 - heroScaleStart) * (localScrollVh / heroScaleRangeVh);
    heroScene.style.setProperty('--hero-scale', heroScale);

    // Circle: from the very start, fade in + rotate + grow all at the same time (0-300vh)
    const circleRangeVh = storyStartVh;
    const circleProgress = localScrollVh < 0
      ? 0
      : localScrollVh >= circleRangeVh
        ? 1
        : localScrollVh / circleRangeVh;

    const fadeInVh = 50;
    const fadeOutStartVh = 260;
    let circleOpacity = 0;
    if (localScrollVh < 0) circleOpacity = 0;
    else if (localScrollVh < fadeInVh) {
      circleOpacity = localScrollVh / fadeInVh;
    } else if (localScrollVh < fadeOutStartVh) {
      circleOpacity = 1;
    } else {
      circleOpacity = Math.max(0, 1 - (localScrollVh - fadeOutStartVh) / (storyStartVh - fadeOutStartVh));
    }
    circleScene.style.opacity = circleOpacity;
    circleScene.style.visibility = circleOpacity > 0.01 ? 'visible' : 'hidden';
    circleScene.style.pointerEvents = circleOpacity > 0.01 ? 'auto' : 'none';
    circleScene.classList.toggle('is-active', circleOpacity > 0.01);

    const scaleStart = 0.12;
    const scaleEnd = 2.8;
    const scale = scaleStart + (scaleEnd - scaleStart) * circleProgress;
    const circleScaleOpacity = circleProgress < 0.85 ? 1 : 1 - (circleProgress - 0.85) / 0.15;

    circleScene.style.setProperty('--circle-scale', scale);

    const circle = circleScene.querySelector('circle-text-animation');
    if (circle) {
      circle.style.setProperty('--circle-rotate', circleProgress);
      circle.style.setProperty('--circle-delayed-rotate', circleProgress);
      circle.style.transform = `scale(${scale})`;
      circle.style.opacity = circleOpacity * circleScaleOpacity;
    }

    // Story slides: active when in their scroll range
    let slideIndex = -1;
    if (localScrollVh >= storyStartVh) {
      slideIndex = Math.floor((localScrollVh - storyStartVh) / 100);
      slideIndex = Math.min(slideIndex, slideScenes.length - 1);
    }

    slideScenes.forEach((scene, i) => {
      const isActive = i === slideIndex;
      scene.classList.toggle('is-active', isActive);
      scene.style.opacity = isActive ? 1 : 0;
      scene.style.visibility = isActive ? 'visible' : 'hidden';
      scene.style.pointerEvents = isActive ? 'auto' : 'none';
    });
  }
}

if (!customElements.get('homepage-scroll-story')) {
  customElements.define('homepage-scroll-story', HomepageScrollStory);
}
